/**
 * Team Bootstrap Service
 * Ensures required documents exist for a new team on first access
 *
 * This service is idempotent - safe to call multiple times
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';

export interface BootstrapResult {
  created: string[];
  skipped: string[];
}

/**
 * Ensure team has all required bootstrap documents
 *
 * Creates (if missing):
 * - /teams/{teamId}/members/{uid} - Team membership
 * - /teams/{teamId}/settings/companyBranding - Company branding defaults
 * - /teams/{teamId}/settings/ui - UI preferences defaults
 *
 * @param teamId - Team ID
 * @param uid - User ID (for membership)
 * @returns Bootstrap result with created/skipped documents
 */
export async function ensureTeamBootstrap(teamId: string, uid: string): Promise<BootstrapResult> {
  const created: string[] = [];
  const skipped: string[] = [];

  try {
    // 1. Ensure team membership exists via CF (Admin SDK — INV-MEMBER-1, INV-IDX-1)
    const memberRef = doc(db, 'teams', teamId, 'members', uid);
    const memberSnap = await getDoc(memberRef);

    if (!memberSnap.exists()) {
      const fns = getFunctions(undefined, 'europe-west1');
      const bootstrapMembership = httpsCallable<
        { teamId: string },
        { ok: boolean; created: boolean }
      >(fns, 'bootstrapTeamOwnerMembershipCallable');
      
      try {
        await bootstrapMembership({ teamId });
        created.push(`members/${uid}`);
        
        // CRITICAL: Force token refresh to pick up custom claims set by CF
        // Without this, user will get permission-denied errors immediately after bootstrap
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
          logger.info('[TeamBootstrap] Token refreshed after membership creation', { teamId, uid });
        }
      } catch (bootstrapErr) {
        logger.error('[TeamBootstrap] Failed to bootstrap membership', { teamId, uid, error: bootstrapErr });
        throw new Error('Failed to create team membership. Please try again.');
      }
    } else {
      skipped.push(`members/${uid}`);
    }

    // 2. Ensure company branding settings exist
    const brandingRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
    const brandingSnap = await getDoc(brandingRef);

    if (!brandingSnap.exists()) {
      await setDoc(brandingRef, {
        companyName: '',
        address: '',
        vatNumber: '',
        kvkNumber: '',
        logoUrl: '',
        updatedAt: serverTimestamp(),
      });
      created.push('settings/companyBranding');
    } else {
      skipped.push('settings/companyBranding');
    }

    // 3. Ensure UI settings exist
    const uiRef = doc(db, 'teams', teamId, 'settings', 'ui');
    const uiSnap = await getDoc(uiRef);

    if (!uiSnap.exists()) {
      await setDoc(uiRef, {
        language: 'nl',
        dateFormat: 'DD-MM-YYYY',
        updatedAt: serverTimestamp(),
      });
      created.push('settings/ui');
    } else {
      skipped.push('settings/ui');
    }

    // Log only if something was created
    if (created.length > 0) {
      logger.info(`[TeamBootstrap] Ensured defaults for team ${teamId}:`, {
        created,
        skipped,
      });
    }

    return { created, skipped };
  } catch (error: unknown) {
    // Handle permission errors gracefully
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      error.code === 'permission-denied'
    ) {
      logger.warn('[TeamBootstrap] Permission denied - user may not be team owner');
      throw new Error('You do not have permission to bootstrap this team');
    }

    logger.error('[TeamBootstrap] Failed to bootstrap team:', error);
    throw error;
  }
}

/**
 * Ensure user-specific preferences exist
 *
 * Creates (if missing):
 * - /teams/{teamId}/users/{uid} - User language preference
 * - /teams/{teamId}/users/{uid}/tablePrefs/{tableKey} - Table preferences
 *
 * @param teamId - Team ID
 * @param uid - User ID
 * @param tableKeys - Table keys to bootstrap (e.g., ['customers', 'vehicles'])
 */
export async function ensureUserPreferences(
  teamId: string,
  uid: string,
  tableKeys: string[] = []
): Promise<void> {
  try {
    // 1. Ensure user language preference exists
    const userRef = doc(db, 'teams', teamId, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(
        userRef,
        {
          language: 'nl',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    // 2. Ensure table preferences exist
    for (const tableKey of tableKeys) {
      const tableRef = doc(db, 'teams', teamId, 'users', uid, 'tablePrefs', tableKey);
      const tableSnap = await getDoc(tableRef);

      if (!tableSnap.exists()) {
        await setDoc(
          tableRef,
          {
            columnOrder: [],
            columnVisibility: {},
            columnSizes: {},
            sortBy: [],
            schemaVersion: 1,
            columnsHash: '',
            updatedAt: Date.now(),
          },
          { merge: true }
        );
      }
    }
  } catch (error: unknown) {
    // Silently fail for user preferences - they're not critical
    if (
      !(error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied')
    ) {
      logger.warn('[TeamBootstrap] Failed to ensure user preferences:', error);
    }
  }
}

/**
 * Check if team is bootstrapped
 *
 * @param teamId - Team ID
 * @returns True if all required documents exist
 */
export async function isTeamBootstrapped(teamId: string): Promise<boolean> {
  try {
    const brandingRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
    const uiRef = doc(db, 'teams', teamId, 'settings', 'ui');

    const [brandingSnap, uiSnap] = await Promise.all([getDoc(brandingRef), getDoc(uiRef)]);

    return brandingSnap.exists() && uiSnap.exists();
  } catch (error) {
    logger.warn('[TeamBootstrap] Failed to check bootstrap status:', error);
    return false;
  }
}
